"use client";

import React, { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { Bell, HelpCircle, Menu, LogOut, User, ExternalLink } from 'lucide-react';

interface TopbarProps {
  title: string;
  subtitle?: string;
  onMobileMenuToggle?: () => void;
  user?: {
    name?: string;
    email?: string;
    avatar?: string;
    tier?: string;
  };
  showUpgradeButton?: boolean;
}

export function Topbar({ title, subtitle, onMobileMenuToggle, user, showUpgradeButton = true }: TopbarProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    // Navigate to Clerk's sign-out page
    window.location.href = '/sign-out';
  };

  return (
    <header className="flex items-center justify-between gap-4 px-4 lg:px-6 py-4 border-b border-white/10 bg-slate-900/30 backdrop-blur-lg">
      <div className="flex items-center gap-4">
        {/* Mobile Menu Button */}
        {onMobileMenuToggle && (
          <button
            onClick={onMobileMenuToggle}
            className="lg:hidden p-2 bg-slate-800/80 backdrop-blur-lg rounded-lg border border-white/10"
          >
            <Menu className="h-5 w-5" />
          </button>
        )}
        <div className="lg:hidden w-8"></div>
        <div>
          <h1 className="text-xl lg:text-2xl font-semibold tracking-tight">{title}</h1>
          {subtitle && (
            <p className="text-sm lg:text-base text-white/60 mt-1">{subtitle}</p>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-3">
        {/* PRO Badge or Upgrade Button - Only show if showUpgradeButton is true */}
        {showUpgradeButton && (
          user?.tier?.toLowerCase() === 'pro' ? (
            // PRO Member Badge - Clickable to manage billing
            <button
              onClick={() => window.location.href = '/billing'}
              className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-orange-400/10 to-pink-400/10 border border-orange-400/30 backdrop-blur-lg hover:border-orange-400/50 hover:bg-gradient-to-r hover:from-orange-400/15 hover:to-pink-400/15 transition-all cursor-pointer"
              title="Manage your PRO subscription"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400"></span>
              </span>
              <span className="text-sm font-semibold bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">
                PRO Member
              </span>
            </button>
          ) : (
            // Upgrade Button - Only show for free tier users
            <button
              onClick={() => window.location.href = '/billing'}
              className="hidden md:flex items-center gap-2 bg-gradient-to-r from-orange-400 to-pink-400 px-4 py-2 rounded-lg font-semibold text-white hover:opacity-90 transition-opacity text-sm"
            >
              Upgrade to Pro
            </button>
          )
        )}

        {/* Notifications */}
        <button className="relative hidden sm:block">
          <Bell className="h-5 w-5" />
          <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-cyan-500"></span>
        </button>

        {/* Help */}
        <HelpCircle className="h-5 w-5 hidden sm:block" />

        {/* User Avatar with Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-slate-600 text-white hover:ring-2 hover:ring-white/20 transition-all focus:outline-none focus:ring-2 focus:ring-white/20"
            aria-label="User menu"
          >
            {user?.avatar ? (
              <Image
                src={user.avatar}
                alt={`${user?.name || 'User'} avatar`}
                fill
                sizes="32px"
                className="object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              user?.name && (
                <span className="text-sm font-medium">
                  {user.name.charAt(0).toUpperCase()}
                </span>
              )
            )}
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-slate-800 border border-white/10 rounded-lg shadow-xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
              {/* User Info Header */}
              <div className="px-4 py-3 border-b border-white/10 bg-slate-800/50">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'User'}</p>
                <p className="text-xs text-white/60 truncate">{user?.email || ''}</p>
                {user?.tier && (
                  user?.tier?.toLowerCase() === 'pro' ? (
                    <span className="inline-flex items-center gap-1.5 mt-2 text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-orange-400/20 to-pink-400/20 border border-orange-400/30 font-semibold">
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-orange-400"></span>
                      </span>
                      <span className="bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">
                        PRO Member
                      </span>
                    </span>
                  ) : (
                    <span className="inline-block mt-2 text-xs px-2.5 py-1 rounded-full bg-slate-700/50 text-white/70 font-medium">
                      Free Plan
                    </span>
                  )
                )}
              </div>

              {/* Menu Items */}
              <div className="py-1">
                <a
                  href="https://myaccount.google.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white hover:bg-white/5 transition-colors"
                >
                  <User className="h-4 w-4" />
                  View Google Profile
                  <ExternalLink className="h-3 w-3 ml-auto" />
                </a>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
